import type {
  ChallengePathAvailability,
  CreateChallengeRewardRequest,
  GamePassUserChallengeProgress,
  GamePassUserEnrollment,
  GamePassUserProgressResponse,
  GamePassUserProgressTask,
  ManualTaskPassUpdateVariables,
  ProgressChallengeViewModel,
  ProgressTaskViewModel,
} from "../../types/gamePass";

const COMPLETED_TRUE_VALUES = new Set<unknown>([
  true,
  1,
  "1",
  "true",
  "True",
  "TRUE",
  "yes",
  "Yes",
]);

const COMPLETED_FALSE_VALUES = new Set<unknown>([
  false,
  0,
  "0",
  "false",
  "False",
  "FALSE",
  "no",
  "No",
]);

export const normalizeCompletedFlag = (
  value: unknown,
): boolean | undefined => {
  if (COMPLETED_FALSE_VALUES.has(value)) {
    return false;
  }
  if (COMPLETED_TRUE_VALUES.has(value)) {
    return true;
  }
  return undefined;
};

type ProgressCompletionRecord = {
  completed?: unknown;
  completed_at?: unknown;
  manually_passed?: unknown;
  reason?: unknown;
  manual_pass_reason?: unknown;
};

export const getManualPassReason = (
  task: ProgressCompletionRecord | Record<string, unknown> | null | undefined,
): string | undefined => {
  if (!task || typeof task !== "object") {
    return undefined;
  }

  const manualPassReason = task.manual_pass_reason;
  if (typeof manualPassReason === "string" && manualPassReason.trim()) {
    return manualPassReason.trim();
  }

  if (normalizeCompletedFlag(task.manually_passed) !== true) {
    return undefined;
  }

  const reason = task.reason;
  if (typeof reason === "string" && reason.trim()) {
    return reason.trim();
  }

  return undefined;
};

export const isManualPassIndicator = (
  task: ProgressCompletionRecord | Record<string, unknown> | null | undefined,
): boolean => {
  if (!task || typeof task !== "object") {
    return false;
  }

  if (normalizeCompletedFlag(task.manually_passed) === true) {
    return true;
  }

  const manualPassReason = task.manual_pass_reason;
  return typeof manualPassReason === "string" && manualPassReason.trim().length > 0;
};

export const isProgressTaskCompleted = (
  task: ProgressCompletionRecord | null | undefined,
): boolean => {
  if (isManualPassIndicator(task)) {
    return true;
  }

  const explicitCompleted = normalizeCompletedFlag(task?.completed);
  if (explicitCompleted != null) {
    return explicitCompleted;
  }

  return task?.completed_at != null && task?.completed_at !== 0;
};

export const isProgressChallengeCompleted = (
  entry: ProgressCompletionRecord | null | undefined,
): boolean => {
  const explicitCompleted = normalizeCompletedFlag(entry?.completed);
  if (explicitCompleted != null) {
    return explicitCompleted;
  }

  return entry?.completed_at != null && entry?.completed_at !== 0;
};

export const getChallengeProgressEntryKey = (
  seasonId: string,
  challengeId: string,
): string => `${seasonId}#${challengeId}`;

export const parseChallengeProgressCompoundKey = (
  compoundKey: string | undefined,
): { seasonId: string; challengeId: string } | null => {
  if (!compoundKey) {
    return null;
  }

  const hashIndex = compoundKey.indexOf("#");
  if (hashIndex <= 0 || hashIndex >= compoundKey.length - 1) {
    return null;
  }

  return {
    seasonId: compoundKey.slice(0, hashIndex),
    challengeId: compoundKey.slice(hashIndex + 1),
  };
};

const normalizeMatchValue = (value: unknown) =>
  typeof value === "string" ? value.trim().toLowerCase() : value;

export const valuesMatch = (left: unknown, right: unknown): boolean => {
  if (left == null || right == null || left === "" || right === "") {
    return false;
  }

  return (
    left === right ||
    normalizeMatchValue(left) === normalizeMatchValue(right)
  );
};

type ChallengeMatchSource = ManualTaskPassUpdateVariables["challenge"];

export const getChallengeCandidateIds = (
  challenge: ChallengeMatchSource | undefined,
  challengeId: string | undefined,
  challengeIndex?: number,
): string[] =>
  Array.from(
    new Set(
      [
        challengeId,
        challenge?.id,
        challenge?.challenge_id,
        challenge?.name,
        typeof challengeIndex === "number" ? String(challengeIndex) : null,
        challenge?.sort_order != null ? String(challenge.sort_order) : null,
      ].filter((value): value is string => value != null && value !== ""),
    ),
  );

export const getProgressEntryChallengeIds = (
  entry: GamePassUserChallengeProgress,
): Set<string> => {
  const entryIds = new Set<string>();

  if (entry.challenge_id) {
    entryIds.add(entry.challenge_id);
  }

  const compoundKey = entry["season_id#challenge_id"];
  if (typeof compoundKey === "string") {
    const hashIndex = compoundKey.indexOf("#");
    if (hashIndex >= 0) {
      entryIds.add(compoundKey.slice(hashIndex + 1));
    }
  }

  return entryIds;
};

const getProgressTaskIdentifier = (
  progressTask: GamePassUserProgressTask | Record<string, unknown>,
): string | null => {
  const record = progressTask as Record<string, unknown>;
  const taskId = progressTask.task_id ?? record.id;
  return typeof taskId === "string" ? taskId : taskId != null ? String(taskId) : null;
};

export const isTaskKeyedProgressEntry = (
  entry: GamePassUserChallengeProgress,
  seasonIds: string[],
  taskId: string,
): boolean => {
  const compoundKey = parseChallengeProgressCompoundKey(
    entry["season_id#challenge_id"],
  );
  if (!compoundKey) {
    return false;
  }

  const seasonMatches =
    seasonIds.length === 0 || seasonIds.includes(compoundKey.seasonId);

  return seasonMatches && valuesMatch(compoundKey.challengeId, taskId);
};

const isChallengeKeyedProgressEntry = (
  entry: GamePassUserChallengeProgress,
  seasonId: string,
  challengeId: string,
  challenge: ChallengeMatchSource | undefined,
  challengeIndex?: number,
): boolean => {
  const candidateIds = getChallengeCandidateIds(
    challenge,
    challengeId,
    challengeIndex,
  );

  const compoundKey = parseChallengeProgressCompoundKey(
    entry["season_id#challenge_id"],
  );
  if (compoundKey) {
    if (compoundKey.seasonId !== seasonId) {
      return false;
    }

    return candidateIds.some((id) =>
      valuesMatch(id, compoundKey.challengeId),
    );
  }

  const entryChallengeId = entry.challenge_id;
  if (!entryChallengeId) {
    return false;
  }

  return candidateIds.some((id) => valuesMatch(id, entryChallengeId));
};

const findManualPassProgressEntryIndex = (
  progressEntries: GamePassUserChallengeProgress[],
  seasonId: string,
  challengeId: string,
  challenge: ChallengeMatchSource | undefined,
  taskId: string,
): number => {
  const taskKeyedIndex = progressEntries.findIndex((entry) =>
    isTaskKeyedProgressEntry(entry, [seasonId], taskId),
  );
  if (taskKeyedIndex >= 0) {
    return taskKeyedIndex;
  }

  const challengeEntryWithTaskIndex = progressEntries.findIndex((entry) => {
    if (
      !isChallengeKeyedProgressEntry(
        entry,
        seasonId,
        challengeId,
        challenge,
        challenge?.sort_order,
      )
    ) {
      return false;
    }

    return (entry.tasks ?? []).some((task) =>
      valuesMatch(getProgressTaskIdentifier(task), taskId),
    );
  });
  if (challengeEntryWithTaskIndex >= 0) {
    return challengeEntryWithTaskIndex;
  }

  return progressEntries.findIndex((entry) =>
    isChallengeKeyedProgressEntry(
      entry,
      seasonId,
      challengeId,
      challenge,
      challenge?.sort_order,
    ),
  );
};

const upsertManualPassTaskOnEntry = (
  entry: GamePassUserChallengeProgress,
  taskId: string,
  reason: string,
): GamePassUserChallengeProgress => {
  const tasks = [...(entry.tasks ?? [])];
  let taskIndex = tasks.findIndex(
    (task) => getProgressTaskIdentifier(task) === taskId,
  );
  const compoundKey = parseChallengeProgressCompoundKey(
    entry["season_id#challenge_id"],
  );
  const isSingleTaskRowForTaskId =
    compoundKey != null &&
    valuesMatch(compoundKey.challengeId, taskId) &&
    tasks.length === 1 &&
    taskIndex < 0;

  if (isSingleTaskRowForTaskId) {
    taskIndex = 0;
  }

  const completedAt = Math.floor(Date.now() / 1000);
  const existingTask = taskIndex >= 0 ? tasks[taskIndex] : undefined;
  const nextTask: GamePassUserProgressTask = {
    ...(existingTask ?? {}),
    task_id: taskId,
    completed: true,
    manually_passed: true,
    manual_pass_reason: reason,
    reason,
    completed_at: existingTask?.completed_at ?? completedAt,
  };

  if (taskIndex >= 0) {
    tasks[taskIndex] = nextTask;
  } else {
    tasks.push(nextTask);
  }

  const allTasksComplete =
    tasks.length > 0 && tasks.every((task) => isProgressTaskCompleted(task));

  return {
    ...entry,
    tasks,
    completed: allTasksComplete ? true : entry.completed,
    completed_at: allTasksComplete
      ? (entry.completed_at ?? completedAt)
      : entry.completed_at,
  };
};

export const applyManualTaskPassToProgressResponse = (
  progressResponse: GamePassUserProgressResponse | undefined,
  {
    taskId,
    reason,
    seasonId,
    challengeId,
    challenge,
    userId,
  }: ManualTaskPassUpdateVariables,
): GamePassUserProgressResponse | undefined => {
  if (!progressResponse?.data) {
    return progressResponse;
  }

  const normalizedReason = String(reason ?? "").trim();
  if (!taskId || !seasonId || !challengeId || !normalizedReason) {
    return progressResponse;
  }

  const progressEntries = progressResponse.data.progress ?? [];
  const targetEntryIndex = findManualPassProgressEntryIndex(
    progressEntries,
    seasonId,
    challengeId,
    challenge,
    taskId,
  );

  if (targetEntryIndex >= 0) {
    const updatedProgress = [...progressEntries];
    updatedProgress[targetEntryIndex] = upsertManualPassTaskOnEntry(
      progressEntries[targetEntryIndex],
      taskId,
      normalizedReason,
    );

    return {
      ...progressResponse,
      data: {
        ...progressResponse.data,
        progress: updatedProgress,
      },
    };
  }

  return {
    ...progressResponse,
    data: {
      ...progressResponse.data,
      progress: [
        ...progressEntries,
        upsertManualPassTaskOnEntry(
          {
            "season_id#challenge_id": getChallengeProgressEntryKey(
              seasonId,
              challengeId,
            ),
            challenge_id: challengeId,
            name: readString(challenge?.name),
            sort_order: challenge?.sort_order,
            completed: false,
            completed_at: null,
            user_id:
              userId ?? progressResponse.data.enrollment?.user_id ?? "",
            tasks: [],
          },
          taskId,
          normalizedReason,
        ),
      ],
    },
  };
};

/** Finite number from a number or numeric string; undefined otherwise. */
export const coerceCount = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const normalizeTierClaims = (
  value: unknown,
): GamePassUserProgressTask["tier_claims"] => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.map((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      return { claimed: false };
    }
    const record = item as Record<string, unknown>;
    const claimed =
      normalizeCompletedFlag(record.claimed) === true ||
      normalizeCompletedFlag(record.reward_claimed) === true;
    return {
      claimed,
      reward_claimed: normalizeCompletedFlag(record.reward_claimed) === true,
    };
  });
};

export const normalizeProgressTask = (
  task: Record<string, unknown> | null | undefined,
): GamePassUserProgressTask | null => {
  if (!task || typeof task !== "object") {
    return null;
  }

  const manualPassReason =
    typeof task.manual_pass_reason === "string" && task.manual_pass_reason.trim()
      ? task.manual_pass_reason.trim()
      : undefined;
  const hasManualPass =
    normalizeCompletedFlag(task.manually_passed) === true ||
    Boolean(manualPassReason);
  const explicitCompleted = normalizeCompletedFlag(task.completed);
  const completed = hasManualPass
    ? true
    : explicitCompleted ??
      (task.completed_at != null && task.completed_at !== 0);

  // The admin progress contract now ships an explicit `count` on every task.
  // Older payloads omit it and only list qualifying contributions on
  // `redeemed_bet_ids` (FE-2544 Casino Challenge) — keep that fallback.
  const redeemedBetIds = Array.isArray(task.redeemed_bet_ids)
    ? (task.redeemed_bet_ids as string[]).filter(
        (betId): betId is string =>
          typeof betId === "string" && betId.trim() !== "",
      )
    : undefined;
  const count =
    coerceCount(task.count) ??
    (redeemedBetIds != null ? redeemedBetIds.length : undefined);
  const targetValue = coerceCount(task.target_value);

  return {
    task_id: (task.task_id ?? task.id) as string | undefined,
    name: (task.name ?? task.task_name) as string | undefined,
    description: task.description as string | undefined,
    index: task.index as number | undefined,
    bet_id: task.bet_id as string | undefined,
    count,
    target_value: targetValue,
    challenge_order: task.challenge_order as number | undefined,
    data_source: task.data_source as string | undefined,
    challenge_type: task.challenge_type as
      | GamePassUserProgressTask["challenge_type"]
      | undefined,
    xp_reward: task.xp_reward as number | undefined,
    streak_count: task.streak_count as number | undefined,
    streak_best: task.streak_best as number | undefined,
    streak_last_date: task.streak_last_date as string | null | undefined,
    streak_broken_on: task.streak_broken_on as string | null | undefined,
    first_login_at: task.first_login_at as string | number | null | undefined,
    last_login_at: task.last_login_at as string | number | null | undefined,
    first_login_source: task.first_login_source as string | null | undefined,
    last_login_source: task.last_login_source as string | null | undefined,
    streaks_broken: task.streaks_broken as number | undefined,
    streak_reset_hour: task.streak_reset_hour as
      | number
      | string
      | null
      | undefined,
    window_time_left: task.window_time_left as string | undefined,
    time_left_in_window: task.time_left_in_window as string | undefined,
    window_closes_at: (task.window_closes_at ?? task.window_ends_at) as
      | string
      | number
      | null
      | undefined,
    window_ends_at: (task.window_ends_at ?? task.window_closes_at) as
      | string
      | number
      | null
      | undefined,
    ...(Array.isArray(task.tiers)
      ? { tiers: task.tiers as GamePassUserProgressTask["tiers"] }
      : {}),
    event_type: task.event_type as string | undefined,
    ...(Array.isArray(task.login_days)
      ? {
          login_days:
            task.login_days as GamePassUserProgressTask["login_days"],
        }
      : {}),
    ...(Array.isArray(task.days)
      ? {
          days: task.days as GamePassUserProgressTask["days"],
        }
      : {}),
    redeemed_bet_ids: redeemedBetIds,
    redeemed_bets: Array.isArray(task.redeemed_bets)
      ? (task.redeemed_bets as GamePassUserProgressTask["redeemed_bets"])
      : undefined,
    ...(Array.isArray(task.distinct_keys)
      ? { distinct_keys: task.distinct_keys as string[] }
      : {}),
    ...(Array.isArray(task.distinct_users)
      ? {
          distinct_users:
            task.distinct_users as GamePassUserProgressTask["distinct_users"],
        }
      : {}),
    completed: Boolean(completed),
    manually_passed: hasManualPass,
    reason: (task.reason ?? task.manual_pass_reason ?? manualPassReason) as
      | string
      | undefined,
    completed_reason: task.completed_reason as string | undefined,
    manual_pass_reason: task.manual_pass_reason as string | undefined,
    completed_at: (task.completed_at ?? null) as number | null,
    reward_claimed: normalizeCompletedFlag(task.reward_claimed) === true,
    tier_claims: normalizeTierClaims(task.tier_claims),
  };
};

const readString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;

const readNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const mapPathToPathAvailability = (
  value: unknown,
): ChallengePathAvailability | undefined => {
  const normalized = readString(value)?.toUpperCase();
  if (
    normalized === "FREE" ||
    normalized === "PREMIUM" ||
    normalized === "BOTH"
  ) {
    return normalized;
  }
  return undefined;
};

export const normalizeProgressEntry = (
  entry: Record<string, unknown>,
  seasonId?: string,
): GamePassUserChallengeProgress => {
  const tasksRaw = entry.tasks ?? entry.task_progress ?? entry.taskProgress;
  const tasks = Array.isArray(tasksRaw)
    ? tasksRaw
        .map((task) =>
          normalizeProgressTask(task as Record<string, unknown>),
        )
        .filter((task): task is GamePassUserProgressTask => task != null)
    : [];

  const explicitCompleted = normalizeCompletedFlag(entry.completed);
  const completed =
    explicitCompleted ??
    (entry.completed_at != null && entry.completed_at !== 0);

  const compoundKey = entry["season_id#challenge_id"];

  const parsedCompoundKey =
    typeof compoundKey === "string"
      ? parseChallengeProgressCompoundKey(compoundKey)
      : null;

  const resolvedChallengeId = (entry.challenge_id ??
    entry.challengeId ??
    parsedCompoundKey?.challengeId) as string | undefined;

  const resolvedSeasonChallengeKey =
    typeof compoundKey === "string"
      ? compoundKey
      : seasonId && resolvedChallengeId
        ? getChallengeProgressEntryKey(seasonId, resolvedChallengeId)
        : undefined;

  return {
    completed: Boolean(completed),
    completed_at: (entry.completed_at ?? null) as number | null,
    user_id: (entry.user_id ?? "") as string,
    version: entry.version as number | undefined,
    xp_earned: entry.xp_earned as number | undefined,
    challenge_id: resolvedChallengeId,
    "season_id#challenge_id": resolvedSeasonChallengeKey,
    name: readString(
      entry.name ?? entry.challenge_name ?? entry.group_name,
    ),
    description: readString(entry.description),
    sort_order: readNumber(entry.sort_order),
    path: readString(entry.path),
    path_availability:
      mapPathToPathAvailability(entry.path_availability) ??
      mapPathToPathAvailability(entry.path),
    img_key: readString(entry.img_key ?? entry.image_url),
    rewards: Array.isArray(entry.rewards)
      ? (entry.rewards as CreateChallengeRewardRequest[])
      : undefined,
    reward_claimed: normalizeCompletedFlag(entry.reward_claimed) === true,
    tasks,
  };
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const isGroupLikeEntry = (entry: unknown): entry is Record<string, unknown> => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return false;
  }

  const record = entry as Record<string, unknown>;
  return (
    record.tasks != null ||
    record.task_progress != null ||
    record.taskProgress != null ||
    record.challenge_id != null ||
    record.challengeId != null ||
    record.challenge_name != null ||
    record["season_id#challenge_id"] != null ||
    record.name != null ||
    record.group_name != null ||
    record.sort_order != null
  );
};

/** Peel nested `{ data: { data: {...} } }` envelopes from progress payloads. */
const unwrapProgressDataBlock = (payload: unknown): Record<string, unknown> => {
  let block = asRecord(payload) ?? {};
  const rootData = asRecord(block.data);
  if (rootData) {
    block = rootData;
  }

  for (let depth = 0; depth < 4; depth += 1) {
    const nested = asRecord(block.data);
    if (!nested) {
      break;
    }

    const nestedHasProgressPayload =
      nested.enrollment != null ||
      nested.progress != null ||
      nested.groups != null ||
      nested.challenges_progress != null ||
      nested.challenge_progress != null ||
      nested.challenges != null ||
      nested.group_progress != null;

    if (nestedHasProgressPayload) {
      block = nested;
      continue;
    }

    break;
  }

  return block;
};

const PROGRESS_COLLECTION_KEYS = [
  "challenges",
  "groups",
  "group_progress",
  "challenges_progress",
  "challenge_progress",
  "progress",
] as const;

/** Unwrap nested `{ groups: [...] }` containers into a flat collection. */
const flattenProgressCollectionSource = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value;
  }

  const record = asRecord(value);
  if (!record) {
    return value;
  }

  for (const key of [
    "challenges",
    "groups",
    "group_progress",
    "items",
    "progress",
  ] as const) {
    const nested = record[key];
    if (Array.isArray(nested)) {
      return nested;
    }

    const nestedRecord = asRecord(nested);
    if (nestedRecord) {
      const flattened = flattenProgressCollectionSource(nested);
      if (Array.isArray(flattened)) {
        return flattened;
      }
    }
  }

  const objectValues = Object.values(record).filter(
    (entry) => entry && typeof entry === "object",
  );
  if (
    objectValues.length > 0 &&
    objectValues.every((entry) => isGroupLikeEntry(entry) || Array.isArray(entry))
  ) {
    return objectValues.flatMap((entry) => (Array.isArray(entry) ? entry : [entry]));
  }

  return value;
};

const hasProgressCollectionEntries = (value: unknown): boolean => {
  if (value == null) {
    return false;
  }

  const flattened = flattenProgressCollectionSource(value);
  return Array.isArray(flattened) && flattened.length > 0;
};

const resolveProgressCollectionSource = (
  ...blocks: Array<Record<string, unknown> | null | undefined>
): unknown => {
  const candidates: unknown[] = [];

  for (const block of blocks) {
    if (!block) {
      continue;
    }

    for (const key of PROGRESS_COLLECTION_KEYS) {
      const candidate = block[key];
      if (candidate != null) {
        candidates.push(candidate);
      }
    }
  }

  for (const candidate of candidates) {
    if (hasProgressCollectionEntries(candidate)) {
      return candidate;
    }
  }

  return candidates[0];
};

const coerceProgressEntries = (
  value: unknown,
  seasonId?: string,
): GamePassUserChallengeProgress[] => {
  const flattened = flattenProgressCollectionSource(value);

  if (Array.isArray(flattened)) {
    return flattened
      .flatMap((entry) => {
        if (Array.isArray(entry)) {
          return entry.filter(
            (nested) => nested && typeof nested === "object" && !Array.isArray(nested),
          );
        }

        return entry && typeof entry === "object" && !Array.isArray(entry)
          ? [entry]
          : [];
      })
      .map((entry) =>
        normalizeProgressEntry(entry as Record<string, unknown>, seasonId),
      );
  }

  if (flattened && typeof flattened === "object") {
    return Object.values(flattened as Record<string, unknown>)
      .filter((entry) => isGroupLikeEntry(entry))
      .map((entry) =>
        normalizeProgressEntry(entry as Record<string, unknown>, seasonId),
      );
  }

  return [];
};

export const normalizeUserProgressResponse = (
  payload: unknown,
): GamePassUserProgressResponse => {
  if (!payload || typeof payload !== "object") {
    return { success: false };
  }

  const record = payload as Record<string, unknown>;
  const dataBlock = unwrapProgressDataBlock(payload);

  const enrollment = (dataBlock.enrollment ?? record.enrollment) as
    | GamePassUserEnrollment
    | undefined;

  const seasonId =
    readString(enrollment?.season_id) ??
    readString(dataBlock.season_id) ??
    readString(asRecord(record.data)?.season_id);

  const progressSource = resolveProgressCollectionSource(
    dataBlock,
    asRecord(record.data),
    record,
  );

  return {
    success: Boolean(record.success ?? true),
    data: {
      enrollment,
      progress: coerceProgressEntries(progressSource, seasonId),
    },
  };
};

export const enrichProgressTasksFromEntry = (
  entry: GamePassUserChallengeProgress,
): GamePassUserProgressTask[] => {
  const compoundKey = parseChallengeProgressCompoundKey(
    entry["season_id#challenge_id"],
  );
  const entryResourceId = compoundKey?.challengeId;
  const shouldUseCompoundKeyAsTaskId =
    entryResourceId != null && (entry.tasks ?? []).length <= 1;

  return (entry.tasks ?? []).map((task, index) => {
    const taskId =
      getProgressTaskIdentifier(task) ??
      (shouldUseCompoundKeyAsTaskId ? entryResourceId : undefined);
    if (!taskId) {
      return task;
    }

    return {
      ...task,
      task_id: taskId,
      index: task.index ?? index,
    };
  });
};

export const progressEntryMatchesConfiguredTask = (
  entry: GamePassUserChallengeProgress,
  seasonIds: string[],
  taskId: string,
): boolean => {
  const compoundKey = parseChallengeProgressCompoundKey(
    entry["season_id#challenge_id"],
  );
  if (compoundKey) {
    const seasonMatches =
      seasonIds.length === 0 || seasonIds.includes(compoundKey.seasonId);
    if (seasonMatches && valuesMatch(compoundKey.challengeId, taskId)) {
      return true;
    }
  }

  return (entry.tasks ?? []).some(
    (task) => valuesMatch(getProgressTaskIdentifier(task), taskId),
  );
};

export const collectManualPassesFromProgressResponse = (
  progressResponse: GamePassUserProgressResponse | undefined,
): ManualTaskPassUpdateVariables[] => {
  const passes: ManualTaskPassUpdateVariables[] = [];

  for (const entry of progressResponse?.data?.progress ?? []) {
    const compoundKey = parseChallengeProgressCompoundKey(
      entry["season_id#challenge_id"],
    );
    const seasonId = compoundKey?.seasonId ?? "";
    const challengeId = compoundKey?.challengeId ?? entry.challenge_id ?? "";
    if (!seasonId || !challengeId) {
      continue;
    }

    for (const task of entry.tasks ?? []) {
      if (!isManualPassIndicator(task)) {
        continue;
      }

      const taskId =
        getProgressTaskIdentifier(task) ?? compoundKey?.challengeId ?? "";
      const reason = getManualPassReason(task);
      if (!taskId || !reason) {
        continue;
      }

      passes.push({
        taskId,
        reason,
        seasonId,
        challengeId,
        userId: entry.user_id,
      });
    }
  }

  return passes;
};

export const mergeManualPassesIntoProgressResponse = (
  target: GamePassUserProgressResponse | undefined,
  source: GamePassUserProgressResponse | undefined,
): GamePassUserProgressResponse | undefined => {
  let merged = target;

  for (const pass of collectManualPassesFromProgressResponse(source)) {
    merged = applyManualTaskPassToProgressResponse(merged, pass) ?? merged;
  }

  return merged;
};

export const buildProgressTaskViewModels = (
  progressEntry: GamePassUserChallengeProgress | null | undefined,
): ProgressTaskViewModel[] =>
  [...(progressEntry?.tasks ?? [])]
    .map((progressTask, index) => {
      const taskId = getProgressTaskIdentifier(progressTask) ?? `task-${index}`;
      const name =
        readString(progressTask.name ?? progressTask.task_name) ??
        "Season";

      return {
        id: taskId,
        name,
        description: readString(progressTask.description),
        data_source: readString(progressTask.data_source),
        xp_reward: readNumber(progressTask.xp_reward),
        challenge_order:
          readNumber(progressTask.challenge_order) ??
          readNumber(progressTask.index) ??
          index,
        progressTask,
      };
    })
    .sort((left, right) => left.challenge_order - right.challenge_order);

export const buildProgressChallengeViewModels = (
  progressEntries: ReadonlyArray<GamePassUserChallengeProgress> = [],
): ProgressChallengeViewModel[] =>
  [...progressEntries]
    .map((entry, index) => {
      const compoundKey = parseChallengeProgressCompoundKey(
        entry["season_id#challenge_id"],
      );
      const challengeId =
        entry.challenge_id ?? compoundKey?.challengeId ?? String(index);

      return {
        id: challengeId,
        challenge_id: challengeId,
        name: entry.name ?? `Challenge ${index + 1}`,
        description: entry.description,
        sort_order: entry.sort_order ?? index,
        path_availability:
          entry.path_availability ?? mapPathToPathAvailability(entry.path),
        rewards: entry.rewards,
        progressEntry: entry,
      };
    })
    .sort((left, right) => left.sort_order - right.sort_order);
